import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Cascader } from "antd";

export const OpportunityTypeCascader = React.memo(
  ({
    opportunityTypes,
    opportunityTypesLoader,
    form,
    canAddEditOpportunity,
    value,
    onChange,
    allowParentSelection = false, // New flag to control parent node selection
  }) => {
    const [cascaderValue, setCascaderValue] = useState([]);
    const [initialValueSet, setInitialValueSet] = useState(false);

    // Transform opportunity types to cascader format
    const opportunityTypesCascaderOptions = useMemo(() => {
      if (!opportunityTypes || opportunityTypes.length === 0) return [];

      // Find parent nodes (types without parentType)
      const parentNodes = opportunityTypes.filter(type => !type.parentType);

      // Transform parent nodes into cascader format with children
      return parentNodes.map(parent => {
        // Find children of this parent
        const children = opportunityTypes
          .filter(type => type.parentType && type.parentType.id === parent.id)
          .map(child => ({
            label: child.name,
            value: child.id,
            key: +child.id,
            isLeaf: true, // Mark as leaf node so it's selectable
          }));

        return {
          label: parent.name,
          value: parent.id,
          key: +parent.id,
          children,
          // Modified logic: If allowParentSelection is true, parent nodes are always selectable
          // If false, parent nodes are only selectable when they have no children
          selectable: allowParentSelection || children.length === 0,
        };
      });
    }, [opportunityTypes, allowParentSelection]);

    // Function to find the cascader path for a given type ID
    const findOpportunityTypePath = useCallback(
      typeId => {
        if (!typeId) return [];

        const opportunityType = opportunityTypes?.find(type => +type.id === +typeId);
        if (!opportunityType) return [];

        // If it has a parent, return parent ID followed by child ID
        if (opportunityType.parentType) {
          return [opportunityType.parentType.id, opportunityType.id];
        }

        // If it's a parent type itself, just return its ID
        return [opportunityType.id];
      },
      [opportunityTypes]
    );

    // Set initial value when types are loaded
    useEffect(() => {
      if (!opportunityTypesLoader && opportunityTypes.length > 0 && value) {
        const path = findOpportunityTypePath(value);
        if (path.length > 0) {
          setCascaderValue(path);
          if (!initialValueSet) {
            form.setFieldValue("opportunityTypeId", path);
            setInitialValueSet(true);
          }
        }
      }
    }, [
      opportunityTypesLoader,
      opportunityTypes,
      value,
      form,
      findOpportunityTypePath,
      initialValueSet,
    ]);

    const handleChange = useCallback(
      newValue => {
        setCascaderValue(newValue);
        form.setFieldValue("opportunityTypeId", newValue);

        if (newValue && newValue.length > 0) {
          onChange(newValue[newValue.length - 1]);
        } else {
          onChange(null);
        }
      },
      [form, onChange]
    );

    // Custom display render function to handle null values
    const displayRender = useCallback(
      labels => {
        // Safely handle null or undefined labels
        if (!labels) {
          return "";
        }

        // Ensure labels is treated as an array
        const safeLabels = Array.isArray(labels) ? labels : [];

        // When no valid labels, use the mapping to show proper text
        if (safeLabels.length === 0 && cascaderValue && cascaderValue.length > 0) {
          try {
            return cascaderValue
              .map(id => {
                if (!id) return "";
                // Find the matching type directly from the flattened options structure
                const matchingType = opportunityTypes.find(type => String(type.id) === String(id));
                return matchingType ? matchingType.name : String(id);
              })
              .filter(Boolean)
              .join(" / ");
          } catch (error) {
            return String(cascaderValue);
          }
        }

        return safeLabels.join(" / ");
      },
      [cascaderValue, opportunityTypes]
    );

    return (
      <Cascader
        options={opportunityTypesCascaderOptions}
        disabled={!canAddEditOpportunity}
        name="opportunityTypeId"
        loading={opportunityTypesLoader}
        placeholder="Select Opportunity Type"
        changeOnSelect={allowParentSelection}
        expandTrigger="hover"
        showSearch
        value={cascaderValue}
        displayRender={displayRender}
        onChange={handleChange}
      />
    );
  }
);

/**
 * Build a tree of opportunity statuses from flat data.
 */
const buildCascaderOptions = flatData => {
  const idMap = {};
  flatData.forEach(item => {
    idMap[item.id] = {
      label: item.name,
      value: +item.id,
      key: +item.id,
      children: [],
    };
  });

  const cascaderOptions = [];

  flatData.forEach(item => {
    if (item.parentStatusId) {
      const parent = idMap[item.parentStatusId];
      if (parent) {
        parent.children.push(idMap[item.id]);
      } else {
        // Orphaned child - treat as top-level
        cascaderOptions.push(idMap[item.id]);
      }
    } else {
      // No parent - top-level
      cascaderOptions.push(idMap[item.id]);
    }
  });

  const removeEmptyChildren = nodes => {
    nodes.forEach(node => {
      if (node.children.length === 0) {
        delete node.children;
        node.isLeaf = true;
      } else {
        removeEmptyChildren(node.children);
      }
    });
  };

  removeEmptyChildren(cascaderOptions);

  return cascaderOptions;
};

/**
 * Recursively find the path from root to the given node ID.
 * @param {Array} tree - Hierarchical cascader options.
 * @param {number} targetId - The final ID to find.
 * @returns {Array} Path of IDs (e.g., [parentId, childId])
 */
const findPathToId = (tree, targetId) => {
  let result = [];
  tree.some(node => {
    if (node.value === targetId) {
      result = [node.value];
      return true;
    }
    if (node.children) {
      const childPath = findPathToId(node.children, targetId);
      if (childPath.length) {
        result = [node.value, ...childPath];
        return true;
      }
    }
    return false;
  });
  return result;
};

/**
 * Final OpportunityStatusCascader component.
 */
export const OpportunityStatusCascader = ({
  leadStatusList,
  canAddEditOpportunity,
  allowParentSelection,
  opportunityStatusLoader,
  value, // single numeric ID, e.g., 2385
  onChange,
  form,
}) => {
  // Build the tree options
  const options = useMemo(() => buildCascaderOptions(leadStatusList), [leadStatusList]);

  // Convert single ID to full path for cascader
  const cascaderValue = useMemo(() => {
    if (value) {
      return findPathToId(options, +value);
    }
    return [];
  }, [value, options]);

  // Handle selection changes
  const handleChange = newPath => {
    const selectedId = newPath[newPath.length - 1];
    if (form) {
      form.setFieldValue("opportunityStatusId", selectedId);
    }
    if (onChange) {
      onChange(selectedId);
    }
  };

  return (
    <Cascader
      options={options}
      disabled={!canAddEditOpportunity}
      name="opportunityStatusId"
      loading={opportunityStatusLoader}
      placeholder="Select Opportunity Status"
      changeOnSelect={allowParentSelection}
      expandTrigger="hover"
      value={cascaderValue}
      onChange={handleChange}
    />
  );
};

OpportunityTypeCascader.displayName = "OpportunityTypeCascader";
OpportunityStatusCascader.displayName = "OpportunityStatusCascader";
